/**
 * CaseAdminPanel — panel visual (sin JSON) para que profesores creen casos.
 *
 * El docente médico NO sabe JSON. Antes el panel pedía pegar un JSON, lo
 * cual era una barrera de entrada enorme. Ahora es un formulario por
 * secciones con catálogos de conceptos LOINC/SNOMED prellenados (vitales,
 * labs, síntomas, imágenes, diagnósticos, antecedentes) y el PatientGraph
 * se arma internamente via CaseBuilder.
 *
 * Flujo:
 *   1. "Nuevo caso" o seleccionar uno existente de la lista izquierda.
 *   2. Completar datos del paciente + agregar hallazgos con los botones "+".
 *   3. "Cargar local" (prueba en la sesión) o "Publicar" (upload al backend).
 *
 * Validación en vivo: al completar cada campo, re-evalúa si se puede publicar.
 */

import { authService } from '../auth/AuthService';
import { caseRepository, type CustomCaseSummary } from '../data/CaseRepository';
import type { AnatomicalSystem, TriagePriority } from '../types/medical';
import {
  ANATOMICAL_SYSTEMS,
  TRIAGE_OPTIONS,
  TYPE_LABELS,
  buildPatientGraph,
  emptyBuilderState,
  genUiId,
  presetsFor,
  slugify,
  validateBuilderState,
  type BuilderFinding,
  type BuilderSeverity,
  type BuilderState,
  type Preset,
} from './CaseBuilder';

const PANEL_ID = 'cgt-case-admin';
const STYLE_ID = 'cgt-case-admin-styles';

const FINDING_TYPES: BuilderFinding['type'][] = [
  'vital_sign', 'symptom', 'laboratory', 'imaging', 'diagnosis', 'history',
];

const SEVERITY_OPTIONS: Array<{ value: BuilderSeverity; label: string }> = [
  { value: 'mild', label: 'Leve' },
  { value: 'moderate', label: 'Moderada' },
  { value: 'severe', label: 'Severa' },
  { value: 'life-threatening', label: 'Riesgo vital' },
];

export class CaseAdminPanel {
  private root: HTMLElement | null = null;
  private state: BuilderState = emptyBuilderState();
  private caseList: CustomCaseSummary[] = [];

  isAvailable(): boolean {
    return authService.currentUser?.role === 'teacher';
  }

  open(): void {
    if (!this.isAvailable()) return;
    if (this.root) return;
    this.state = emptyBuilderState();
    this.injectStyles();

    const root = document.createElement('div');
    root.id = PANEL_ID;
    root.innerHTML = `
      <div class="cgt-ca-backdrop" data-close></div>
      <div class="cgt-ca-card">
        <header>
          <div>
            <h2>Creador de casos clínicos</h2>
            <p>Armá casos paso a paso. El docente elige del catálogo clínico, el simulador arma el resto.</p>
          </div>
          <button type="button" class="cgt-ca-close" data-close aria-label="Cerrar">×</button>
        </header>
        <div class="cgt-ca-layout">
          <aside class="cgt-ca-sidebar">
            <div class="cgt-ca-sidebar-header">
              <h3>Casos publicados</h3>
              <button type="button" data-refresh class="cgt-ca-icon" title="Recargar lista">⟳</button>
            </div>
            <button type="button" class="cgt-ca-new" data-new>＋ Nuevo caso</button>
            <div class="cgt-ca-list" data-list>
              <div class="cgt-ca-empty">Cargando…</div>
            </div>
          </aside>
          <div class="cgt-ca-body" data-body></div>
        </div>
        <footer class="cgt-ca-footer">
          <div class="cgt-ca-status" data-status>Completá los datos del paciente para empezar.</div>
          <div class="cgt-ca-actions">
            <button type="button" class="cgt-ca-secondary" data-load>Cargar local (preview)</button>
            <button type="button" class="cgt-ca-primary" data-publish>Publicar</button>
          </div>
        </footer>
      </div>
    `;

    root.querySelectorAll<HTMLElement>('[data-close]').forEach(el =>
      el.addEventListener('click', () => this.close()),
    );
    root.querySelector<HTMLButtonElement>('[data-refresh]')?.addEventListener('click', () => void this.refreshList());
    root.querySelector<HTMLButtonElement>('[data-new]')?.addEventListener('click', () => {
      this.state = emptyBuilderState();
      this.renderBody();
      this.updateStatus();
    });
    root.querySelector<HTMLButtonElement>('[data-load]')?.addEventListener('click', () => void this.loadLocalPreview());
    root.querySelector<HTMLButtonElement>('[data-publish]')?.addEventListener('click', () => void this.publish());

    document.body.appendChild(root);
    requestAnimationFrame(() => root.classList.add('cgt-ca-visible'));
    this.root = root;

    this.renderBody();
    this.updateStatus();
    void this.refreshList();
  }

  close(): void {
    if (!this.root) return;
    const el = this.root;
    el.classList.remove('cgt-ca-visible');
    setTimeout(() => el.remove(), 200);
    this.root = null;
  }

  // ─────────────────────────────────────────────────────────────
  // Lista de casos publicados
  // ─────────────────────────────────────────────────────────────

  private async refreshList(): Promise<void> {
    const list = this.root?.querySelector<HTMLElement>('[data-list]');
    if (!list) return;
    list.innerHTML = '<div class="cgt-ca-empty">Cargando…</div>';
    this.caseList = await caseRepository.listRemote();
    if (this.caseList.length === 0) {
      list.innerHTML = '<div class="cgt-ca-empty">Aún no hay casos publicados.</div>';
      return;
    }
    list.innerHTML = this.caseList.map(c => this.renderCaseRow(c)).join('');
    list.querySelectorAll<HTMLElement>('[data-case-slug]').forEach(el => {
      el.addEventListener('click', () => void this.loadCaseForEdit(el.dataset.caseSlug!));
    });
    list.querySelectorAll<HTMLElement>('[data-del-slug]').forEach(el => {
      el.addEventListener('click', async (evt) => {
        evt.stopPropagation();
        const slug = el.dataset.delSlug;
        if (!slug) return;
        if (!confirm(`¿Eliminar el caso "${slug}"? Esta acción es irreversible.`)) return;
        try {
          await caseRepository.deleteRemote(slug);
          await this.refreshList();
          this.setStatus(`Caso ${slug} eliminado.`, 'ok');
        } catch (e) {
          this.setStatus(e instanceof Error ? e.message : String(e), 'err');
        }
      });
    });
  }

  private renderCaseRow(c: CustomCaseSummary): string {
    const color = TRIAGE_OPTIONS.find(o => o.value === c.triage_priority)?.color ?? '#8E8E93';
    const mine = authService.currentUser?.id === c.author_id;
    return `
      <div class="cgt-ca-case" data-case-slug="${esc(c.slug)}">
        <span class="cgt-ca-dot" style="background:${color}"></span>
        <div class="cgt-ca-case-info">
          <strong>${esc(c.display_label)}</strong>
          <small>${esc(c.slug)} · ${esc(c.author_name ?? 'anón')}</small>
        </div>
        ${mine ? `<button type="button" class="cgt-ca-del" data-del-slug="${esc(c.slug)}" title="Eliminar">×</button>` : ''}
      </div>
    `;
  }

  private async loadCaseForEdit(slug: string): Promise<void> {
    try {
      const full = await caseRepository.getRemote(slug);
      // Deserializar PatientGraph → BuilderState (solo campos que el builder maneja)
      this.state = {
        slug,
        displayLabel: this.caseList.find(c => c.slug === slug)?.display_label ?? slug,
        age: full.demographics.age,
        sex: full.demographics.sex,
        weight: full.demographics.weight,
        chiefComplaint: full.chiefComplaint ?? '',
        triagePriority: full.triagePriority,
        findings: full.nodes
          .filter(n => FINDING_TYPES.includes(n.type as BuilderFinding['type']))
          .map(n => ({
            uiId: genUiId(),
            type: n.type as BuilderFinding['type'],
            conceptDisplay: n.concept.display,
            conceptCode: n.concept.code,
            conceptSystem: n.concept.system,
            anatomicalSystem: n.system,
            numericValue: n.valueQuantity?.value,
            unit: n.valueQuantity?.unit,
            refLow: n.referenceRange?.low,
            refHigh: n.referenceRange?.high,
            textValue: n.valueString,
            severity: n.severity as BuilderSeverity | undefined,
          })),
      };
      this.renderBody();
      this.updateStatus();
    } catch (e) {
      this.setStatus(e instanceof Error ? e.message : String(e), 'err');
    }
  }

  // ─────────────────────────────────────────────────────────────
  // Render del cuerpo: secciones del formulario
  // ─────────────────────────────────────────────────────────────

  private renderBody(): void {
    const body = this.root?.querySelector<HTMLElement>('[data-body]');
    if (!body) return;
    body.innerHTML = `
      ${this.renderIdentitySection()}
      ${this.renderPatientSection()}
      ${this.renderFindingsSection()}
    `;
    this.attachBodyHandlers(body);
  }

  private renderIdentitySection(): string {
    return `
      <section class="cgt-ca-section">
        <h3>Identificación del caso</h3>
        <div class="cgt-ca-grid cgt-ca-grid-2">
          <label class="cgt-ca-field">
            <span>Título del caso</span>
            <input type="text" data-field="displayLabel" value="${esc(this.state.displayLabel)}" placeholder="Ej: IAM en paciente de 67 años" />
          </label>
          <label class="cgt-ca-field">
            <span>Slug (identificador)</span>
            <input type="text" data-field="slug" value="${esc(this.state.slug)}" placeholder="iam-adulto-mayor" />
            <small>Se autogenera del título. Solo minúsculas, dígitos y guiones.</small>
          </label>
        </div>
      </section>
    `;
  }

  private renderPatientSection(): string {
    const triageOpts = TRIAGE_OPTIONS.map(o =>
      `<option value="${o.value}" ${this.state.triagePriority === o.value ? 'selected' : ''}>${esc(o.label)}</option>`
    ).join('');
    return `
      <section class="cgt-ca-section">
        <h3>Datos del paciente</h3>
        <div class="cgt-ca-grid cgt-ca-grid-4">
          <label class="cgt-ca-field">
            <span>Edad (años)</span>
            <input type="number" min="0" max="120" step="1" data-field="age" value="${this.state.age}" />
          </label>
          <label class="cgt-ca-field">
            <span>Sexo</span>
            <select data-field="sex">
              <option value="male" ${this.state.sex === 'male' ? 'selected' : ''}>Masculino</option>
              <option value="female" ${this.state.sex === 'female' ? 'selected' : ''}>Femenino</option>
              <option value="other" ${this.state.sex === 'other' ? 'selected' : ''}>Otro</option>
            </select>
          </label>
          <label class="cgt-ca-field">
            <span>Peso (kg)</span>
            <input type="number" min="0" max="300" step="1" data-field="weight" value="${this.state.weight ?? ''}" placeholder="opcional" />
          </label>
          <label class="cgt-ca-field">
            <span>Triage inicial</span>
            <select data-field="triagePriority">${triageOpts}</select>
          </label>
        </div>
        <label class="cgt-ca-field cgt-ca-field-wide">
          <span>Motivo de consulta</span>
          <textarea data-field="chiefComplaint" rows="2" placeholder="Ej: Dolor torácico opresivo de 40 minutos, irradia a brazo izquierdo.">${esc(this.state.chiefComplaint)}</textarea>
        </label>
      </section>
    `;
  }

  private renderFindingsSection(): string {
    const cards = this.state.findings.map((f, i) => this.renderFindingCard(f, i)).join('');
    const addBtns = FINDING_TYPES.map(t => {
      const meta = TYPE_LABELS[t];
      return `<button type="button" class="cgt-ca-add" data-add="${t}" style="--accent:${meta.color}">${meta.icon} ${esc(meta.label)}</button>`;
    }).join('');
    return `
      <section class="cgt-ca-section">
        <div class="cgt-ca-section-head">
          <h3>Hallazgos clínicos <small>(${this.state.findings.length})</small></h3>
          <div class="cgt-ca-section-hint">
            Agregá vitales, síntomas, labs, imágenes y el diagnóstico final. La IA usará estos datos; mínimo 3, con al menos 1 diagnóstico.
          </div>
        </div>
        <div class="cgt-ca-add-row">${addBtns}</div>
        <div class="cgt-ca-findings">${cards || '<div class="cgt-ca-empty-state">Todavía no agregaste hallazgos. Empezá con un vital o un síntoma.</div>'}</div>
      </section>
    `;
  }

  private renderFindingCard(f: BuilderFinding, index: number): string {
    const meta = TYPE_LABELS[f.type];
    const presets = presetsFor(f.type);
    const presetOpts = ['<option value="">— Elegir del catálogo —</option>']
      .concat(presets.map(p => {
        const selected = (f.conceptCode === p.code && f.conceptSystem === p.system) ? 'selected' : '';
        return `<option value="${esc(p.key)}" ${selected}>${esc(p.display)}</option>`;
      }))
      .concat(['<option value="__custom__">Otro (manual)…</option>'])
      .join('');

    const showNumeric = f.type === 'vital_sign' || f.type === 'laboratory';
    const showText = f.type === 'symptom' || f.type === 'imaging' || f.type === 'history';
    const showSeverity = f.type === 'symptom' || f.type === 'diagnosis' || f.type === 'laboratory';
    const showAnatomy = true;

    const severityOpts = ['<option value="">— sin severidad —</option>']
      .concat(SEVERITY_OPTIONS.map(s =>
        `<option value="${s.value}" ${f.severity === s.value ? 'selected' : ''}>${esc(s.label)}</option>`
      )).join('');

    const anatomyOpts = ['<option value="">— sin sistema —</option>']
      .concat(ANATOMICAL_SYSTEMS.map(s =>
        `<option value="${s}" ${f.anatomicalSystem === s ? 'selected' : ''}>${esc(s)}</option>`
      )).join('');

    return `
      <article class="cgt-ca-card-finding" data-finding="${f.uiId}" style="--accent:${meta.color}">
        <header>
          <span class="cgt-ca-badge">${meta.icon} ${esc(meta.label)}</span>
          <strong class="cgt-ca-finding-title">${esc(f.conceptDisplay || 'Sin título')}</strong>
          <button type="button" class="cgt-ca-remove" data-remove="${f.uiId}" title="Quitar">×</button>
        </header>
        <div class="cgt-ca-grid cgt-ca-grid-2">
          <label class="cgt-ca-field">
            <span>Catálogo</span>
            <select data-finding-preset="${f.uiId}">${presetOpts}</select>
          </label>
          <label class="cgt-ca-field">
            <span>Nombre clínico</span>
            <input type="text" data-finding-field="conceptDisplay" data-finding-id="${f.uiId}"
              value="${esc(f.conceptDisplay)}" placeholder="Nombre del hallazgo" />
          </label>
        </div>
        <div class="cgt-ca-grid ${showNumeric ? 'cgt-ca-grid-4' : 'cgt-ca-grid-2'}">
          ${showAnatomy ? `
            <label class="cgt-ca-field">
              <span>Sistema anatómico</span>
              <select data-finding-field="anatomicalSystem" data-finding-id="${f.uiId}">${anatomyOpts}</select>
            </label>
          ` : ''}
          ${showSeverity ? `
            <label class="cgt-ca-field">
              <span>Severidad</span>
              <select data-finding-field="severity" data-finding-id="${f.uiId}">${severityOpts}</select>
            </label>
          ` : ''}
          ${showNumeric ? `
            <label class="cgt-ca-field">
              <span>Valor</span>
              <input type="number" step="any" data-finding-field="numericValue" data-finding-id="${f.uiId}"
                value="${f.numericValue ?? ''}" placeholder="110" />
            </label>
            <label class="cgt-ca-field">
              <span>Unidad</span>
              <input type="text" data-finding-field="unit" data-finding-id="${f.uiId}"
                value="${esc(f.unit ?? '')}" placeholder="bpm, mg/dL…" />
            </label>
            <label class="cgt-ca-field">
              <span>Rango normal (mín)</span>
              <input type="number" step="any" data-finding-field="refLow" data-finding-id="${f.uiId}"
                value="${f.refLow ?? ''}" placeholder="opcional" />
            </label>
            <label class="cgt-ca-field">
              <span>Rango normal (máx)</span>
              <input type="number" step="any" data-finding-field="refHigh" data-finding-id="${f.uiId}"
                value="${f.refHigh ?? ''}" placeholder="opcional" />
            </label>
          ` : ''}
        </div>
        ${showText ? `
          <label class="cgt-ca-field cgt-ca-field-wide">
            <span>Descripción</span>
            <textarea data-finding-field="textValue" data-finding-id="${f.uiId}" rows="2"
              placeholder="${f.type === 'symptom' ? 'Ej: opresivo, 8/10, irradia a brazo izquierdo' : f.type === 'imaging' ? 'Ej: elevación ST ≥2mm en DII-DIII-aVF' : 'Detalle del antecedente'}">${esc(f.textValue ?? '')}</textarea>
          </label>
        ` : ''}
        <small class="cgt-ca-finding-meta">
          ${f.conceptCode ? `Código: <code>${esc(f.conceptCode)}</code>` : 'Sin código clínico'} ·
          <span>Posición #${index + 1}</span>
        </small>
      </article>
    `;
  }

  // ─────────────────────────────────────────────────────────────
  // Handlers del body (inputs + botones)
  // ─────────────────────────────────────────────────────────────

  private attachBodyHandlers(body: HTMLElement): void {
    // Campos de top-level (identity + patient section)
    body.querySelectorAll<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>('[data-field]').forEach(el => {
      const key = el.dataset.field!;
      const evt = (el.tagName === 'SELECT' || el.type === 'number') ? 'change' : 'input';
      el.addEventListener(evt, () => this.applyTopField(key, el));
      el.addEventListener('blur', () => this.applyTopField(key, el));
    });

    // Botones "+ Tipo"
    body.querySelectorAll<HTMLButtonElement>('[data-add]').forEach(btn => {
      btn.addEventListener('click', () => {
        this.addFinding(btn.dataset.add as BuilderFinding['type']);
      });
    });

    // Botones de remove en cada finding
    body.querySelectorAll<HTMLButtonElement>('[data-remove]').forEach(btn => {
      btn.addEventListener('click', () => {
        this.removeFinding(btn.dataset.remove!);
      });
    });

    // Preset dropdown por finding
    body.querySelectorAll<HTMLSelectElement>('[data-finding-preset]').forEach(sel => {
      sel.addEventListener('change', () => {
        this.applyPreset(sel.dataset.findingPreset!, sel.value);
      });
    });

    // Campos por finding
    body.querySelectorAll<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>('[data-finding-field]').forEach(el => {
      const id = el.dataset.findingId!;
      const key = el.dataset.findingField!;
      const evt = (el.tagName === 'SELECT' || (el as HTMLInputElement).type === 'number') ? 'change' : 'input';
      el.addEventListener(evt, () => this.applyFindingField(id, key, el));
      el.addEventListener('blur', () => this.applyFindingField(id, key, el));
    });
  }

  private applyTopField(key: string, el: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement): void {
    const val = el.value;
    const s = this.state;
    switch (key) {
      case 'displayLabel':
        s.displayLabel = val;
        // Auto-sugerir slug si el usuario no lo tocó aún
        if (!s.slug) {
          s.slug = slugify(val);
          const slugInput = this.root?.querySelector<HTMLInputElement>('[data-field="slug"]');
          if (slugInput) slugInput.value = s.slug;
        }
        break;
      case 'slug': s.slug = slugify(val); el.value = s.slug; break;
      case 'age': s.age = Number(val) || 0; break;
      case 'sex': s.sex = val as BuilderState['sex']; break;
      case 'weight': s.weight = val === '' ? undefined : Number(val); break;
      case 'chiefComplaint': s.chiefComplaint = val; break;
      case 'triagePriority': s.triagePriority = val as TriagePriority; break;
    }
    this.updateStatus();
  }

  private addFinding(type: BuilderFinding['type']): void {
    const uiId = genUiId();
    const meta = TYPE_LABELS[type];
    this.state.findings.push({
      uiId,
      type,
      conceptDisplay: meta.label,
    });
    this.renderBody();
    this.updateStatus();
    // Scroll al nuevo
    const card = this.root?.querySelector(`[data-finding="${uiId}"]`);
    card?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  private removeFinding(uiId: string): void {
    this.state.findings = this.state.findings.filter(f => f.uiId !== uiId);
    this.renderBody();
    this.updateStatus();
  }

  private applyPreset(uiId: string, presetKey: string): void {
    const finding = this.state.findings.find(f => f.uiId === uiId);
    if (!finding) return;
    if (!presetKey || presetKey === '__custom__') {
      // No preset seleccionado; dejar campos actuales
      return;
    }
    const preset: Preset | undefined = presetsFor(finding.type).find(p => p.key === presetKey);
    if (!preset) return;
    finding.conceptDisplay = preset.display;
    finding.conceptCode = preset.code;
    finding.conceptSystem = preset.system;
    finding.anatomicalSystem = preset.anatomicalSystem;
    if (preset.unit !== undefined) finding.unit = preset.unit;
    if (preset.refLow !== undefined) finding.refLow = preset.refLow;
    if (preset.refHigh !== undefined) finding.refHigh = preset.refHigh;
    if (preset.defaultSeverity) finding.severity = preset.defaultSeverity;
    this.renderBody();
    this.updateStatus();
  }

  private applyFindingField(uiId: string, key: string, el: HTMLElement): void {
    const finding = this.state.findings.find(f => f.uiId === uiId);
    if (!finding) return;
    const val = (el as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement).value;
    switch (key) {
      case 'conceptDisplay':
        finding.conceptDisplay = val;
        // Refrescar el título del card sin re-render pesado
        const title = this.root?.querySelector(`[data-finding="${uiId}"] .cgt-ca-finding-title`);
        if (title) title.textContent = val || 'Sin título';
        break;
      case 'anatomicalSystem':
        finding.anatomicalSystem = (val || undefined) as AnatomicalSystem | undefined;
        break;
      case 'severity':
        finding.severity = (val || undefined) as BuilderSeverity | undefined;
        break;
      case 'numericValue':
        finding.numericValue = val === '' ? undefined : Number(val);
        break;
      case 'unit':
        finding.unit = val || undefined;
        break;
      case 'refLow':
        finding.refLow = val === '' ? undefined : Number(val);
        break;
      case 'refHigh':
        finding.refHigh = val === '' ? undefined : Number(val);
        break;
      case 'textValue':
        finding.textValue = val;
        break;
    }
    this.updateStatus();
  }

  // ─────────────────────────────────────────────────────────────
  // Acciones: preview local / publicar
  // ─────────────────────────────────────────────────────────────

  private async loadLocalPreview(): Promise<void> {
    const err = validateBuilderState(this.state);
    if (err) {
      this.setStatus(err, 'err');
      return;
    }
    const graph = buildPatientGraph(this.state);
    const loader = (window as unknown as { app?: { loadCaseFromObject?: (d: unknown) => Promise<void> } }).app;
    if (!loader?.loadCaseFromObject) {
      this.setStatus('La app no está disponible para preview.', 'err');
      return;
    }
    this.setStatus('Cargando preview en el simulador…', 'info');
    await loader.loadCaseFromObject(graph);
    this.setStatus('Preview cargado. Cerrá el panel para probarlo.', 'ok');
  }

  private async publish(): Promise<void> {
    const err = validateBuilderState(this.state);
    if (err) {
      this.setStatus(err, 'err');
      return;
    }
    const graph = buildPatientGraph(this.state);
    try {
      this.setStatus('Publicando al servidor…', 'info');
      const created = await caseRepository.createRemote(this.state.slug, this.state.displayLabel, graph);
      this.setStatus(`Caso "${created.slug}" publicado. Alumnos ya pueden cargarlo.`, 'ok');
      await this.refreshList();
    } catch (e) {
      this.setStatus(e instanceof Error ? e.message : String(e), 'err');
    }
  }

  private updateStatus(): void {
    const err = validateBuilderState(this.state);
    const publishBtn = this.root?.querySelector<HTMLButtonElement>('[data-publish]');
    const loadBtn = this.root?.querySelector<HTMLButtonElement>('[data-load]');
    if (publishBtn) publishBtn.disabled = err !== null;
    if (loadBtn) loadBtn.disabled = err !== null;
    if (err) {
      this.setStatus(err, 'info');
    } else {
      const n = this.state.findings.length;
      const dx = this.state.findings.find(f => f.type === 'diagnosis')?.conceptDisplay ?? '—';
      this.setStatus(`Listo para publicar: ${n} hallazgos, dx "${dx}".`, 'ok');
    }
  }

  private setStatus(msg: string, kind: 'ok' | 'err' | 'info'): void {
    const el = this.root?.querySelector<HTMLElement>('[data-status]');
    if (!el) return;
    el.textContent = msg;
    el.dataset.kind = kind;
  }

  // ─────────────────────────────────────────────────────────────
  // Estilos
  // ─────────────────────────────────────────────────────────────

  private injectStyles(): void {
    if (document.getElementById(STYLE_ID)) return;
    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = styles;
    document.head.appendChild(style);
  }
}

function esc(text: string | undefined | null): string {
  if (text === undefined || text === null) return '';
  return String(text).replace(/[&<>"']/g, c => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
  }[c] ?? c));
}

export const caseAdminPanel = new CaseAdminPanel();

const styles = `
  #${PANEL_ID} {
    position: fixed; inset: 0; z-index: 15000;
    font-family: -apple-system, BlinkMacSystemFont, 'Inter', sans-serif;
    color: #f5f5f7;
    opacity: 0; transition: opacity 0.2s;
  }
  #${PANEL_ID}.cgt-ca-visible { opacity: 1; }
  #${PANEL_ID} .cgt-ca-backdrop {
    position: absolute; inset: 0; background: rgba(0,0,0,0.55);
    backdrop-filter: blur(4px);
  }
  #${PANEL_ID} .cgt-ca-card {
    position: relative; max-width: 1100px; width: 94vw; max-height: 92vh;
    margin: 4vh auto; background: #0a0a0a;
    border: 0.5px solid rgba(255,255,255,0.1);
    border-radius: 18px; display: flex; flex-direction: column;
    overflow: hidden; box-shadow: 0 20px 60px rgba(0,0,0,0.5);
  }
  #${PANEL_ID} header {
    display: flex; align-items: start; justify-content: space-between;
    padding: 20px 24px; border-bottom: 0.5px solid rgba(255,255,255,0.06);
  }
  #${PANEL_ID} header h2 { font-size: 18px; font-weight: 700; letter-spacing: -0.3px; }
  #${PANEL_ID} header p { font-size: 12px; color: #a1a1a6; margin-top: 4px; max-width: 520px; }
  #${PANEL_ID} .cgt-ca-close {
    width: 28px; height: 28px; border-radius: 50%;
    background: rgba(255,255,255,0.06); border: none; cursor: pointer;
    color: #a1a1a6; font-size: 18px; line-height: 1; flex-shrink: 0;
  }
  #${PANEL_ID} .cgt-ca-layout {
    display: grid; grid-template-columns: 280px 1fr;
    flex: 1; overflow: hidden;
  }
  #${PANEL_ID} .cgt-ca-sidebar {
    border-right: 0.5px solid rgba(255,255,255,0.06);
    display: flex; flex-direction: column;
    background: rgba(255,255,255,0.015); overflow: hidden;
  }
  #${PANEL_ID} .cgt-ca-sidebar-header {
    display: flex; align-items: center; justify-content: space-between;
    padding: 14px 16px 8px;
  }
  #${PANEL_ID} .cgt-ca-sidebar-header h3 {
    font-size: 11px; text-transform: uppercase; letter-spacing: 1px;
    color: #6e6e73; font-weight: 600;
  }
  #${PANEL_ID} .cgt-ca-icon {
    width: 24px; height: 24px; border: none; border-radius: 50%;
    background: rgba(255,255,255,0.05); color: #a1a1a6; cursor: pointer;
    font-size: 13px;
  }
  #${PANEL_ID} .cgt-ca-new {
    margin: 0 12px 10px;
    padding: 10px 12px;
    background: linear-gradient(135deg, #2997FF, #BF5AF2); color: #fff;
    border: none; border-radius: 10px; cursor: pointer;
    font-weight: 600; font-size: 13px; font-family: inherit;
  }
  #${PANEL_ID} .cgt-ca-new:hover { filter: brightness(1.1); }
  #${PANEL_ID} .cgt-ca-list { flex: 1; overflow-y: auto; padding: 4px 8px 12px; }
  #${PANEL_ID} .cgt-ca-empty {
    padding: 20px 14px; font-size: 12px; color: #6e6e73; text-align: center;
  }
  #${PANEL_ID} .cgt-ca-case {
    display: flex; align-items: center; gap: 10px;
    padding: 10px; border-radius: 8px; cursor: pointer;
    transition: background 0.15s;
  }
  #${PANEL_ID} .cgt-ca-case:hover { background: rgba(255,255,255,0.04); }
  #${PANEL_ID} .cgt-ca-dot { width: 10px; height: 10px; border-radius: 50%; flex-shrink: 0; }
  #${PANEL_ID} .cgt-ca-case-info { flex: 1; min-width: 0; }
  #${PANEL_ID} .cgt-ca-case-info strong {
    display: block; font-size: 12px; color: #f5f5f7;
    overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  }
  #${PANEL_ID} .cgt-ca-case-info small {
    display: block; font-size: 10px; color: #6e6e73; margin-top: 2px;
  }
  #${PANEL_ID} .cgt-ca-del {
    width: 22px; height: 22px; border: none; border-radius: 50%;
    background: rgba(255,69,58,0.15); color: #FF6961; cursor: pointer;
    font-size: 13px; line-height: 1;
  }

  /* BODY (formulario principal) */
  #${PANEL_ID} .cgt-ca-body {
    overflow-y: auto; padding: 18px 24px 24px;
    display: flex; flex-direction: column; gap: 20px;
  }
  #${PANEL_ID} .cgt-ca-section {
    background: rgba(255,255,255,0.02);
    border: 0.5px solid rgba(255,255,255,0.06);
    border-radius: 12px; padding: 16px 18px;
  }
  #${PANEL_ID} .cgt-ca-section h3 {
    font-size: 13px; font-weight: 700; margin-bottom: 12px;
    color: #f5f5f7; letter-spacing: -0.2px;
  }
  #${PANEL_ID} .cgt-ca-section h3 small {
    font-size: 11px; font-weight: 500; color: #6e6e73; margin-left: 4px;
  }
  #${PANEL_ID} .cgt-ca-section-head {
    display: flex; justify-content: space-between; align-items: baseline;
    margin-bottom: 10px; gap: 14px; flex-wrap: wrap;
  }
  #${PANEL_ID} .cgt-ca-section-head h3 { margin-bottom: 0; }
  #${PANEL_ID} .cgt-ca-section-hint {
    font-size: 11.5px; color: #8e8e93; max-width: 540px;
  }
  #${PANEL_ID} .cgt-ca-grid { display: grid; gap: 12px; }
  #${PANEL_ID} .cgt-ca-grid-2 { grid-template-columns: 1fr 1fr; }
  #${PANEL_ID} .cgt-ca-grid-4 { grid-template-columns: repeat(4, 1fr); }
  @media (max-width: 780px) {
    #${PANEL_ID} .cgt-ca-grid-2, #${PANEL_ID} .cgt-ca-grid-4 { grid-template-columns: 1fr; }
  }
  #${PANEL_ID} .cgt-ca-field {
    display: flex; flex-direction: column; gap: 4px;
    min-width: 0;
  }
  #${PANEL_ID} .cgt-ca-field-wide { margin-top: 12px; }
  #${PANEL_ID} .cgt-ca-field span {
    font-size: 10.5px; color: #8e8e93;
    text-transform: uppercase; letter-spacing: 0.5px; font-weight: 600;
  }
  #${PANEL_ID} .cgt-ca-field small { font-size: 10.5px; color: #6e6e73; }
  #${PANEL_ID} .cgt-ca-field input,
  #${PANEL_ID} .cgt-ca-field select,
  #${PANEL_ID} .cgt-ca-field textarea {
    background: #050505; color: #f5f5f7;
    border: 0.5px solid rgba(255,255,255,0.08);
    border-radius: 8px; padding: 9px 12px;
    font-family: inherit; font-size: 12.5px; line-height: 1.4;
    transition: border-color 0.15s, background 0.15s;
  }
  #${PANEL_ID} .cgt-ca-field textarea { resize: vertical; min-height: 56px; font-family: inherit; }
  #${PANEL_ID} .cgt-ca-field input:focus,
  #${PANEL_ID} .cgt-ca-field select:focus,
  #${PANEL_ID} .cgt-ca-field textarea:focus {
    outline: none; border-color: #2997FF; background: #111;
  }

  /* Botones de agregar hallazgo */
  #${PANEL_ID} .cgt-ca-add-row {
    display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 14px;
  }
  #${PANEL_ID} .cgt-ca-add {
    padding: 8px 14px; border-radius: 8px;
    background: rgba(255,255,255,0.03);
    color: #f5f5f7;
    border: 0.5px solid var(--accent, rgba(255,255,255,0.1));
    cursor: pointer; font-size: 12px; font-weight: 600; font-family: inherit;
    transition: background 0.15s;
  }
  #${PANEL_ID} .cgt-ca-add:hover {
    background: color-mix(in srgb, var(--accent, #2997FF) 20%, transparent);
  }

  /* Cards de hallazgos */
  #${PANEL_ID} .cgt-ca-findings {
    display: flex; flex-direction: column; gap: 10px;
  }
  #${PANEL_ID} .cgt-ca-empty-state {
    padding: 24px; text-align: center; color: #8e8e93; font-size: 12.5px;
    background: rgba(255,255,255,0.015);
    border-radius: 10px; border: 0.5px dashed rgba(255,255,255,0.1);
  }
  #${PANEL_ID} .cgt-ca-card-finding {
    background: #050505;
    border: 0.5px solid rgba(255,255,255,0.08);
    border-left: 2px solid var(--accent, #2997FF);
    border-radius: 10px; padding: 12px 14px;
    display: flex; flex-direction: column; gap: 10px;
  }
  #${PANEL_ID} .cgt-ca-card-finding header {
    padding: 0; border: none;
    display: flex; align-items: center; gap: 10px;
  }
  #${PANEL_ID} .cgt-ca-badge {
    padding: 3px 10px; border-radius: 10px;
    background: color-mix(in srgb, var(--accent, #2997FF) 15%, transparent);
    color: var(--accent, #2997FF);
    font-size: 10.5px; font-weight: 700; letter-spacing: 0.3px;
    flex-shrink: 0;
  }
  #${PANEL_ID} .cgt-ca-finding-title {
    flex: 1; font-size: 13px; font-weight: 600; color: #f5f5f7;
    overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  }
  #${PANEL_ID} .cgt-ca-remove {
    width: 22px; height: 22px; border: none; border-radius: 50%;
    background: rgba(255,69,58,0.1); color: #FF6961; cursor: pointer;
    font-size: 14px; line-height: 1;
  }
  #${PANEL_ID} .cgt-ca-finding-meta {
    font-size: 10.5px; color: #6e6e73;
  }
  #${PANEL_ID} .cgt-ca-finding-meta code {
    font-family: 'JetBrains Mono', monospace;
    background: rgba(41,151,255,0.08); color: #2997FF;
    padding: 1px 6px; border-radius: 4px; font-size: 10.5px;
  }

  /* Footer */
  #${PANEL_ID} .cgt-ca-footer {
    display: flex; align-items: center; justify-content: space-between;
    padding: 12px 24px; gap: 16px;
    border-top: 0.5px solid rgba(255,255,255,0.06);
    background: rgba(255,255,255,0.02);
  }
  #${PANEL_ID} .cgt-ca-status {
    font-size: 11.5px; color: #a1a1a6; flex: 1; min-width: 0;
  }
  #${PANEL_ID} .cgt-ca-status[data-kind="ok"] { color: #30D158; }
  #${PANEL_ID} .cgt-ca-status[data-kind="err"] { color: #FF6961; }
  #${PANEL_ID} .cgt-ca-status[data-kind="info"] { color: #FF9F0A; }
  #${PANEL_ID} .cgt-ca-actions { display: flex; gap: 8px; flex-shrink: 0; }
  #${PANEL_ID} .cgt-ca-secondary,
  #${PANEL_ID} .cgt-ca-primary {
    padding: 9px 16px; border-radius: 8px; cursor: pointer;
    font-size: 12.5px; font-weight: 600; font-family: inherit;
    transition: background 0.15s, opacity 0.15s;
  }
  #${PANEL_ID} .cgt-ca-secondary {
    background: rgba(255,255,255,0.05); color: #a1a1a6;
    border: 0.5px solid rgba(255,255,255,0.08);
  }
  #${PANEL_ID} .cgt-ca-secondary:hover:not(:disabled) {
    color: #fff; background: rgba(255,255,255,0.1);
  }
  #${PANEL_ID} .cgt-ca-primary {
    background: #2997FF; color: #fff; border: none;
  }
  #${PANEL_ID} .cgt-ca-primary:hover:not(:disabled) { background: #0a84ff; }
  #${PANEL_ID} .cgt-ca-primary:disabled,
  #${PANEL_ID} .cgt-ca-secondary:disabled {
    opacity: 0.4; cursor: not-allowed;
  }
`;
